// Huddledin Module Entry Point
import { initReports } from './features/reports/index.js';
import { initHomework, mountHomeworkCreateModal, openTemplatePicker, renderHomeworkSpecList, renderHomeworkDetail, renderHomeworkParent, renderTemplatesPage, loadExerciseTemplates, saveExerciseTemplate, updateExerciseTemplate, deleteExerciseTemplate, incrementExerciseTemplateUseCount, mountHomeworkParent, refreshHomeworkParent, destroyHomeworkParent, isHomeworkParentMounted } from './features/homework/index.js';
import { initCsvImport } from './features/csv-import/index.js';
import { initClinicDashboard } from './features/clinic-dashboard/index.js';
import { initNotes } from './features/notes/index.js';
import { initClinicBranding } from './features/clinic-branding/index.js';
import { initHelp } from './features/help/index.js';
import { initOnboarding } from './features/onboarding/index.js';   // ONB-PARENT: the parent first-run wizard (installs window.HUD_ONB at import)
import './features/search-clear/index.js'; // F-107: registers window.HUD_SEARCH_CLEAR (HUD.withClear)
import { loadBundle } from './features/notes/rich-loader.js';
import { initClientStorage } from './features/client-storage/index.js';

console.log('[Huddledin] Module system loaded');

// F-84: the template-upload flow below speaks the app language through the dictionary (EN fallback until a Hebrew string exists).
const T = (k, p) => window.HUD?.T?.(k, p) || k;
const _locDate = (d, o, l) => window.HUD?._locDate ? window.HUD._locDate(d, o, l) : d.toLocaleDateString(l, o);
const _locTime = (d, o, l) => window.HUD?._locTime ? window.HUD._locTime(d, o, l) : d.toLocaleTimeString(l, o);

// S-1: the private-client-folder helpers have no HUD dependency at init, so they are ready before the first render.
initClientStorage();

if (window.HUD) {
  initReports();
  initHomework();
  initCsvImport();
  initClinicDashboard();
  initNotes();
  initClinicBranding();
  initHelp();
  initOnboarding();
} else {
  window.addEventListener('hud-ready', () => { initReports(); initHomework(); initCsvImport(); initClinicDashboard(); initNotes(); initClinicBranding(); initHelp(); initOnboarding(); });
}

// Homework module public API
window.HUD_HOMEWORK = { mountHomeworkCreateModal, openTemplatePicker, renderHomeworkSpecList, renderHomeworkDetail, renderHomeworkParent, renderTemplatesPage, loadExerciseTemplates, saveExerciseTemplate, updateExerciseTemplate, deleteExerciseTemplate, incrementExerciseTemplateUseCount, mountHomeworkParent, refreshHomeworkParent, destroyHomeworkParent, isHomeworkParentMounted };

// ── Lazy loader for file-parser bundle ──
// Queue item 24: the same retry-safe loadBundle as the tiptap bundle (item 21). The old loader reused a FAILED <script>
// tag and waited on load/error events that had already fired, so every later template upload hung. The bundle sets
// HUD_PARSE_FILE and HUD_EXTRACT_TEMPLATE together; loadBundle waits on the first.
async function loadFileParserBundle() {
  if (window.HUD_PARSE_FILE && window.HUD_EXTRACT_TEMPLATE) return;
  await loadBundle('/file-parser.bundle.js', 'HUD_PARSE_FILE');
}
window.HUD_LOAD_FILE_PARSER = loadFileParserBundle;

// ── Lazy loader for tiptap bundle ──
// Queue item 21 (2026-09-30): the notes' retry-safe loader. The old one reused a FAILED <script> tag and waited on
// load/error events that had already fired, so every later open hung. loadBundle removes a failed tag (a retry
// re-requests the file) and shares one in-flight request between concurrent calls.
async function loadTiptapBundle() {
  await loadBundle('/tiptap.bundle.js', 'HUD_TIPTAP');
}

async function getTiptapAPI() {
  await loadTiptapBundle();
  return window.HUD_TIPTAP;
}
window.HUD_TIPTAP_API = getTiptapAPI;

// ── Tiptap editor gate modal ──
async function openTiptapGateModal(opts = {}) {
  // R3a (D3): a report needs a patient (reports_patient_xor + RLS). A new report without one never opens — ask the
  // user to select a patient (existing string). Templates, read-only views and saved reports (their row carries the
  // patient) are unaffected.
  if (!opts.templateMode && !opts.readOnly && !opts.draftId && !opts.reportId && !opts.childId && !opts.specialistPatientId) {
    window.HUD?.toast?.(window.HUD?.T?.('spec_dt_select_patient') || 'Select a patient', 'info');
    return;
  }
  const overlay = document.createElement('div');
  overlay.style.cssText = 'position:fixed;inset:0;z-index:450;background:rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;padding:16px;';
  const card = document.createElement('div');
  card.style.cssText = 'background:#f5fafa;border-radius:14px;width:100%;max-width:900px;max-height:calc(100vh - 32px);overflow-y:auto;padding:24px;position:relative;box-shadow:0 8px 32px rgba(0,0,0,.2);';

  // Phase 2 Q8: the report editor uses the notes sheet's window (src/features/notes/styles.js): >= 1024 a 24px inset,
  // 768–1023 a 16px inset, the full height; phones (< 768) keep the card as it was. The page inside stays A4-wide and
  // centred (the room beside it is for Phase 3's Images panel). Preview / the finalized view live in the same window.
  const _fitWindow = () => {
    const w = window.innerWidth;
    if (w >= 768) {
      const pad = w >= 1024 ? 24 : 16;
      Object.assign(overlay.style, { padding: pad + 'px', alignItems: 'stretch', justifyContent: 'stretch' });
      Object.assign(card.style, { maxWidth: 'none', maxHeight: 'none', borderRadius: (w >= 1024 ? 16 : 14) + 'px' });
    } else {
      Object.assign(overlay.style, { padding: '16px', alignItems: 'center', justifyContent: 'center' });
      Object.assign(card.style, { maxWidth: '900px', maxHeight: 'calc(100vh - 32px)', borderRadius: '14px' });
    }
  };
  _fitWindow();
  window.addEventListener('resize', _fitWindow);

  let gateEditor = null;
  let _closing = false;

  async function closeModal() {
    if (_closing) return;
    if (gateEditor) {
      // R1 (2026-09-26): _gateSave/_gateFlush RESOLVE ok/failed (they never throw), so the old try/catch never
      // prompted and a failed save closed the editor and lost the text. Now: wait for an in-flight save, try one
      // flush save if anything is unsaved, and only a failure shows the existing "close anyway?" confirm
      // (Stay keeps the editor + text). A clean report closes at once with no write.
      _closing = true;
      let saved = true;
      try {
        const flush = gateEditor._gateFlush || gateEditor._gateSave;
        saved = (await flush?.()) !== false;
      } catch (e) {
        console.error('❌ report close flush:', e);
        saved = false;
      } finally {
        _closing = false;
      }
      if (!saved) {
        const proceed = await new Promise(resolve => {
          const c = window.HUD?.openConfirm;
          if (c) {
            let resolved = false;
            c(T('unsaved_title'), T('rb_close_unsaved_body'), true, () => { resolved = true; resolve(true); });
            const ov = document.body.lastElementChild;
            if (ov?.classList.contains('overlay')) {
              const obs = new MutationObserver(() => {
                if (!ov.isConnected && !resolved) { obs.disconnect(); resolve(false); }
              });
              obs.observe(document.body, { childList: true });
            }
          } else {
            resolve(confirm(T('rb_close_unsaved_body')));
          }
        });
        if (!proceed) return;
        try { gateEditor._gateDiscard?.(); } catch (_) {} // R3e: "close anyway" drops the on-device copy too
      }
      gateEditor._gateCleanup?.();
    }
    overlay.remove();
    window.removeEventListener('resize', _fitWindow);
    window.HUD_REPORTS?.invalidateReportsCache?.();
    try { window.HUD?.re?.(); } catch (_) {}
    // SC4b: the gate cleanup above cleared S._activeGate, so drain a deferred SW reload now that
    // the report surface is closed and clean.
    try { window.HUD?._maybeSwReload?.(); } catch (_) {}
  }

  const editorHost = document.createElement('div');
  card.appendChild(editorHost);
  overlay.appendChild(card);
  overlay.onclick = (e) => { if (e.target === overlay) closeModal(); };
  document.body.appendChild(overlay);
  const { mountGateEditor } = await window.HUD_TIPTAP_API();
  gateEditor = await mountGateEditor(editorHost, { ...opts, _closeModal: () => closeModal(), _overlay: overlay });
}
window.HUD_openTiptapGate = openTiptapGateModal;

// ── Upload template flow ──
const LS_TEMPLATE_KEY = 'huddledin.template_draft';

function _pickFile(accept) {
  return new Promise(resolve => {
    const inp = document.createElement('input');
    inp.type = 'file';
    inp.accept = accept || '.pdf,.docx';
    inp.style.display = 'none';
    inp.onchange = () => { resolve(inp.files?.[0] || null); inp.remove(); };
    document.body.appendChild(inp);
    inp.click();
    setTimeout(() => { if (!inp.files?.length) inp.remove(); }, 120000);
  });
}

// F-84: language-independent. A parser error carries rbLocal + rbKey (its message is already in the app language: shown as is, except the
// PDF-init failure, which has a longer helpful sentence of its own). A server error is chosen by its HTTP status ONLY (never by its English text);
// for a 400 the document's own length (the server's bounds are 200 and 40000 characters) says whether it was too short or too long.
function _friendlyError(err) {
  if (err?.rbLocal) return err.rbKey === 'rb_import_pdf_init_fail' ? T('tpl_err_pdf_init') : (err.message || T('tpl_err_generic'));
  const st = err?.status;
  if (st === 429) return T('tpl_err_rate');
  if (st === 401) return T('tpl_err_session');
  if (st === 400 && typeof err.textLen === 'number' && err.textLen < 200) return T('tpl_err_short');
  if (st === 400 && typeof err.textLen === 'number' && err.textLen > 40000) return T('tpl_err_long');
  if (st === 502) return T('tpl_err_unexpected');
  return T('tpl_err_generic');
}

function _openLoadingModal() {
  const overlay = document.createElement('div');
  overlay.style.cssText = 'position:fixed;inset:0;z-index:500;background:rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;padding:16px;';
  const card = document.createElement('div');
  card.style.cssText = 'background:#fff;border-radius:14px;width:100%;max-width:420px;padding:28px;text-align:center;box-shadow:0 8px 32px rgba(0,0,0,.2);';
  const titleEl = document.createElement('div');
  titleEl.style.cssText = 'font-size:16px;font-weight:700;color:#0f1a18;margin-bottom:16px;';
  titleEl.textContent = T('tpl_creating');
  card.appendChild(titleEl);
  const barOuter = document.createElement('div');
  barOuter.style.cssText = 'height:8px;border-radius:4px;background:#e8f4f2;overflow:hidden;margin-bottom:12px;';
  const barInner = document.createElement('div');
  barInner.style.cssText = 'height:100%;border-radius:4px;background:#0d9488;width:0%;transition:width .3s ease;';
  barOuter.appendChild(barInner);
  card.appendChild(barOuter);
  const msgEl = document.createElement('div');
  msgEl.style.cssText = 'font-size:13px;color:#64748b;margin-bottom:6px;';
  msgEl.textContent = T('tpl_preparing');
  card.appendChild(msgEl);
  const timerEl = document.createElement('div');
  timerEl.style.cssText = 'font-size:12px;color:#94a3b8;';
  card.appendChild(timerEl);
  overlay.appendChild(card);
  document.body.appendChild(overlay);

  const start = Date.now();
  let pct = 0;
  let done = false;
  const tick = setInterval(() => {
    if (done) return;
    const elapsed = (Date.now() - start) / 1000;
    timerEl.textContent = '(' + Math.floor(elapsed) + 's)';
    if (pct < 95) {
      pct = Math.min(95, pct + (95 - pct) * 0.08);
      barInner.style.width = pct.toFixed(1) + '%';
    }
    if (elapsed > 20 && pct >= 90) titleEl.textContent = T('tpl_still_working');
    if (elapsed > 45) msgEl.textContent = T('tpl_taking_longer');
  }, 300);

  return {
    update({ message }) { msgEl.textContent = message; },
    complete() {
      done = true; clearInterval(tick);
      barInner.style.width = '100%';
      setTimeout(() => overlay.remove(), 500);
    },
    error(message) {
      done = true; clearInterval(tick);
      card.textContent = '';
      const icon = document.createElement('div');
      icon.style.cssText = 'font-size:36px;margin-bottom:12px;';
      icon.textContent = '\u26a0\ufe0f';
      card.appendChild(icon);
      const errMsg = document.createElement('div');
      errMsg.style.cssText = 'font-size:14px;color:#ef4444;margin-bottom:16px;line-height:1.5;';
      errMsg.textContent = message;
      card.appendChild(errMsg);
      const btns = document.createElement('div');
      btns.style.cssText = 'display:flex;gap:10px;justify-content:center;';
      const retry = document.createElement('button');
      retry.textContent = T('tpl_try_again');
      retry.style.cssText = 'padding:8px 18px;border:none;border-radius:8px;background:#0d9488;color:#fff;font-size:14px;cursor:pointer;font-family:inherit;';
      retry.onclick = () => { overlay.remove(); handleUploadTemplate(); };
      btns.appendChild(retry);
      const closeBtn = document.createElement('button');
      closeBtn.textContent = T('btn_close');
      closeBtn.style.cssText = 'padding:8px 18px;border:1px solid #d1e0dd;border-radius:8px;background:#fff;font-size:14px;cursor:pointer;font-family:inherit;color:#64748b;';
      closeBtn.onclick = () => overlay.remove();
      btns.appendChild(closeBtn);
      card.appendChild(btns);
    },
  };
}

function _relTime(ts) {   // F-84: dictionary + the app-language date/time helpers (the same sentence parts the report-draft dialog uses)
  const d = new Date(ts);
  const now = new Date();
  const diffM = Math.floor((now - d) / 60000);
  if (diffM < 1) return T('rb_just_now');
  if (diffM < 60) return T('rb_minutes_ago', { n: diffM });
  const time = _locTime(d, { hour: '2-digit', minute: '2-digit' }, []);
  if (d.toDateString() === now.toDateString()) return T('rb_today_at', { time });
  const y = new Date(now); y.setDate(y.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return T('rb_yesterday_at', { time });
  return _locDate(d);
}

async function handleUploadTemplate() {
  // Check for localStorage backup
  try {
    const raw = localStorage.getItem(LS_TEMPLATE_KEY);
    if (raw) {
      const backup = JSON.parse(raw);
      if (backup.content && backup.updatedAt && (Date.now() - new Date(backup.updatedAt).getTime()) < 24 * 60 * 60 * 1000) {
        const choice = await new Promise(resolve => {
          const c = window.HUD?.openModal;
          if (!c) return resolve('new');
          c(T('tpl_resume_title'), (mb, close) => {
            const msg = document.createElement('div');
            msg.style.cssText = 'margin-bottom:20px;color:#334155;font-size:14px;line-height:1.5;';
            msg.textContent = T('tpl_resume_body', { when: _relTime(backup.updatedAt) });
            mb.appendChild(msg);
            const row = document.createElement('div');
            row.style.cssText = 'display:flex;gap:10px;justify-content:flex-end;flex-wrap:wrap;';
            const cancelBtn = document.createElement('button');
            cancelBtn.className = 'btn-md btn-ghost';
            cancelBtn.textContent = T('btn_cancel');
            cancelBtn.onclick = () => { close(); resolve('cancel'); };
            row.appendChild(cancelBtn);
            const newBtn = document.createElement('button');
            newBtn.className = 'btn-md btn-secondary';
            newBtn.textContent = T('rb_start_new');
            newBtn.onclick = () => { close(); localStorage.removeItem(LS_TEMPLATE_KEY); resolve('new'); };
            row.appendChild(newBtn);
            const resumeBtn = document.createElement('button');
            resumeBtn.className = 'btn-md btn-primary';
            resumeBtn.textContent = T('tpl_resume_btn');
            resumeBtn.onclick = () => { close(); resolve('resume'); };
            row.appendChild(resumeBtn);
            mb.appendChild(row);
          }, 420);
        });
        if (choice === 'cancel') return;
        if (choice === 'resume') {
          openTiptapGateModal({ templateMode: true, templateContent: backup.content, sourceFileName: backup.sourceFileName });
          return;
        }
      } else {
        localStorage.removeItem(LS_TEMPLATE_KEY);
      }
    }
  } catch (_) {}

  const file = await _pickFile('.pdf,.docx');
  if (!file) return;

  const loading = _openLoadingModal();
  try {
    loading.update({ message: T('tpl_step_parser') });
    await loadFileParserBundle();
    loading.update({ message: T('tpl_step_reading') });
    const parsed = await window.HUD_PARSE_FILE(file);
    loading.update({ message: T('tpl_step_analyzing') });
    const specialty = window.HUD?.session?.profession || null;
    const result = await window.HUD_EXTRACT_TEMPLATE({ text: parsed.text, specialty, fileName: parsed.fileName });
    loading.complete();
    openTiptapGateModal({ templateMode: true, templateSections: result.sections, sourceFileName: parsed.fileName });
  } catch (err) {
    if (err && err.aiGate) { loading.complete(); window.HUD?._showSpecAiUpgradeModal?.('Reports'); return; }   // QA-01
    console.error('[upload-template]', err);
    loading.error(_friendlyError(err));
  }
}
window.HUD_UPLOAD_TEMPLATE = handleUploadTemplate;

// URL-param triggers
const _draftParam = new URLSearchParams(window.location.search).get('draft');
if (_draftParam) {
  const launch = () => openTiptapGateModal({ draftId: _draftParam });
  if (document.readyState === 'complete') launch();
  else window.addEventListener('load', launch);
}
